/**
 * Studio staff (managers, supervisors, accountants, etc.) — the
 * people whose monthly salary the studio pays out of overhead.
 *
 * Collections:
 *   - `staff/{staffId}`                              — Staff
 *   - `staffAttendance/{staffId}_{YYYY-MM-DD}`        — StaffAttendance
 *
 * Web port of mobile `src/features/staff/staff.ts` + `types.ts`.
 * Same Firestore docs / shape / id strategy so the two clients
 * agree on every row.
 *
 * Permission: gated by Firestore rule `canFinance(orgId)` which
 * includes SA / Admin / Manager / Accountant.
 */
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { auth, db } from '@/lib/firebase';
import type { OrgFinance } from '@/lib/orgFinances';

// ───── Types ─────────────────────────────────────────────────

export type PayUnit = 'day' | 'month';
export type StaffAttendanceStatus = 'present' | 'half' | 'absent';

export type Staff = {
  id: string;
  orgId: string;
  name: string;
  phone?: string;
  partyId?: string;
  role: string;
  monthlySalary: number;
  payUnit: PayUnit;
  isOrgMember: boolean;
  orgMemberUid?: string | null;
  archivedAt?: { toMillis(): number } | null;
  lastPayrollMonth?: string;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
  updatedAt: { toMillis(): number } | null;
};

export type StaffAttendance = {
  id: string;
  staffId: string;
  orgId: string;
  date: string; // 'YYYY-MM-DD'
  status: StaffAttendanceStatus;
  hours?: number | null;
  note?: string;
  recordedBy: string;
  recordedAt: { toMillis(): number } | null;
};

export type CreateStaffInput = {
  orgId: string;
  name: string;
  phone?: string;
  partyId?: string;
  role: string;
  monthlySalary: number;
  payUnit: PayUnit;
  isOrgMember: boolean;
  orgMemberUid?: string | null;
  createdBy: string;
};

// ───── Helpers ──────────────────────────────────────────────

export function monthKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

export function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Conventional Indian-payroll figure — excludes Sundays. */
export const WORKING_DAYS_PER_MONTH = 22;

export function dailyRate(staff: Pick<Staff, 'monthlySalary' | 'payUnit'>): number {
  // For both pay units we derive daily from monthly so the math is
  // a single source of truth. payUnit just controls how payroll is
  // billed.
  return staff.monthlySalary / WORKING_DAYS_PER_MONTH;
}

export function computePayroll(
  staff: Pick<Staff, 'monthlySalary' | 'payUnit'>,
  presentDays: number,
  halfDays: number,
): number {
  if (staff.payUnit === 'month') {
    const earnedDays = presentDays + halfDays * 0.5;
    return Math.round((staff.monthlySalary * earnedDays) / WORKING_DAYS_PER_MONTH);
  }
  return Math.round((presentDays + halfDays * 0.5) * dailyRate(staff));
}

function endOfMonthLocal(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

// ───── Staff CRUD ──────────────────────────────────────────

function requireUser(): string {
  const u = auth.currentUser;
  if (!u) throw new Error('You must be signed in.');
  return u.uid;
}

export async function createStaff(input: CreateStaffInput): Promise<string> {
  requireUser();
  const cleanPhone = input.phone?.trim();
  const ref = await addDoc(collection(db, 'staff'), {
    orgId: input.orgId,
    name: input.name.trim(),
    role: input.role.trim(),
    monthlySalary: input.monthlySalary,
    payUnit: input.payUnit,
    isOrgMember: input.isOrgMember,
    orgMemberUid: input.orgMemberUid ?? null,
    archivedAt: null,
    lastPayrollMonth: null,
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...(cleanPhone ? { phone: cleanPhone } : {}),
    ...(input.partyId ? { partyId: input.partyId } : {}),
  });
  return ref.id;
}

export async function updateStaff(
  id: string,
  patch: Partial<
    Pick<Staff, 'name' | 'role' | 'monthlySalary' | 'payUnit' | 'orgMemberUid' | 'phone'>
  >,
): Promise<void> {
  requireUser();
  const data: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (patch.name !== undefined) data.name = patch.name.trim();
  if (patch.role !== undefined) data.role = patch.role.trim();
  if (patch.monthlySalary !== undefined) data.monthlySalary = patch.monthlySalary;
  if (patch.payUnit !== undefined) data.payUnit = patch.payUnit;
  if (patch.orgMemberUid !== undefined) data.orgMemberUid = patch.orgMemberUid;
  if (patch.phone !== undefined) data.phone = patch.phone?.trim() ?? '';
  await updateDoc(doc(db, 'staff', id), data);
}

export async function archiveStaff(id: string): Promise<void> {
  requireUser();
  await updateDoc(doc(db, 'staff', id), {
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function unarchiveStaff(id: string): Promise<void> {
  requireUser();
  await updateDoc(doc(db, 'staff', id), {
    archivedAt: null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteStaff(id: string): Promise<void> {
  requireUser();
  await deleteDoc(doc(db, 'staff', id));
}

// ───── Attendance ──────────────────────────────────────────

export async function markStaffAttendance(args: {
  staffId: string;
  orgId: string;
  date: string; // 'YYYY-MM-DD'
  status: StaffAttendanceStatus;
  hours?: number;
  note?: string;
}): Promise<void> {
  const uid = requireUser();
  const id = `${args.staffId}_${args.date}`;
  await setDoc(
    doc(db, 'staffAttendance', id),
    {
      staffId: args.staffId,
      orgId: args.orgId,
      date: args.date,
      status: args.status,
      hours: args.hours ?? null,
      note: args.note ?? '',
      recordedBy: uid,
      recordedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

export async function clearStaffAttendance(args: {
  staffId: string;
  date: string;
}): Promise<void> {
  requireUser();
  await deleteDoc(doc(db, 'staffAttendance', `${args.staffId}_${args.date}`));
}

// ───── Payroll posting ─────────────────────────────────────

export type PayrollPreviewRow = {
  staff: Staff;
  presentDays: number;
  halfDays: number;
  absentDays: number;
  amount: number;
};

export function buildPayrollPreview(
  staffList: Staff[],
  attendanceByStaff: Record<string, StaffAttendance[]>,
): PayrollPreviewRow[] {
  return staffList
    .filter((s) => !s.archivedAt)
    .map((s) => {
      const att = attendanceByStaff[s.id] ?? [];
      let presentDays = 0;
      let halfDays = 0;
      let absentDays = 0;
      for (const a of att) {
        if (a.status === 'present') presentDays += 1;
        else if (a.status === 'half') halfDays += 1;
        else if (a.status === 'absent') absentDays += 1;
      }
      const amount = computePayroll(s, presentDays, halfDays);
      return { staff: s, presentDays, halfDays, absentDays, amount };
    });
}

/** Post the month's payroll. For each row, writes one `orgFinances`
 *  entry (category=salary, payee=staff.name) and stamps
 *  `lastPayrollMonth = YYYY-MM` on the staff doc. Idempotent —
 *  re-running for the same month skips rows whose lastPayrollMonth
 *  already matches. */
export async function postMonthlyPayroll(args: {
  orgId: string;
  month: Date;
  rows: PayrollPreviewRow[];
}): Promise<{ posted: number; skipped: number }> {
  const uid = requireUser();
  const mk = monthKey(args.month);
  const batch = writeBatch(db);
  let posted = 0;
  let skipped = 0;

  for (const row of args.rows) {
    if (row.staff.lastPayrollMonth === mk) {
      skipped += 1;
      continue;
    }
    if (row.amount <= 0) {
      // Zero-amount staff still get their month marked done so the
      // button hides for fully-absent staff next render.
      batch.update(doc(db, 'staff', row.staff.id), {
        lastPayrollMonth: mk,
        updatedAt: serverTimestamp(),
      });
      skipped += 1;
      continue;
    }

    const financeRef = doc(collection(db, 'orgFinances'));
    const note =
      `Payroll · ${mk} · ${row.presentDays} present` +
      (row.halfDays > 0 ? ` + ${row.halfDays} half` : '') +
      (row.absentDays > 0 ? ` · ${row.absentDays} absent` : '');
    const finance: Omit<OrgFinance, 'id'> = {
      orgId: args.orgId,
      kind: 'expense',
      category: 'salary',
      amount: row.amount,
      paidAt: Timestamp.fromDate(endOfMonthLocal(args.month)),
      payee: row.staff.name,
      payeeUid: row.staff.orgMemberUid ?? null,
      paymentMethod: 'bank',
      note,
      createdBy: uid,
      createdAt: null,
      updatedAt: null,
    };
    batch.set(financeRef, {
      ...finance,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    batch.update(doc(db, 'staff', row.staff.id), {
      lastPayrollMonth: mk,
      updatedAt: serverTimestamp(),
    });
    posted += 1;
  }

  await batch.commit();
  return { posted, skipped };
}

// ───── Hooks ───────────────────────────────────────────────

export type UseStaffResult = { data: Staff[]; loading: boolean };

export function useStaff(orgId: string | null | undefined): UseStaffResult {
  const [data, setData] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(collection(db, 'staff'), where('orgId', '==', orgId)),
      (snap) => {
        const rows: Staff[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Staff, 'id'>),
        }));
        rows.sort((a, b) => a.name.localeCompare(b.name));
        setData(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useStaff] snapshot error:', err);
        setData([]);
        setLoading(false);
      },
    );
  }, [orgId]);

  return { data, loading };
}

export type UseStaffAttendanceResult = {
  byStaff: Record<string, StaffAttendance[]>;
  loading: boolean;
};

/** Live attendance for a calendar month. Returns rows grouped by
 *  staffId for cheap lookup in the Payroll preview + attendance UI. */
export function useStaffAttendance(
  orgId: string | null | undefined,
  month: Date,
): UseStaffAttendanceResult {
  const [rows, setRows] = useState<StaffAttendance[]>([]);
  const [loading, setLoading] = useState(true);

  // Stable month start/end keys so the listener doesn't re-attach
  // on every render (Date identity flips every re-render otherwise).
  const startKey = dateKey(new Date(month.getFullYear(), month.getMonth(), 1));
  const endKey = dateKey(new Date(month.getFullYear(), month.getMonth() + 1, 0));

  useEffect(() => {
    if (!orgId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(
        collection(db, 'staffAttendance'),
        where('orgId', '==', orgId),
        where('date', '>=', startKey),
        where('date', '<=', endKey),
      ),
      (snap) => {
        const next: StaffAttendance[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<StaffAttendance, 'id'>),
        }));
        setRows(next);
        setLoading(false);
      },
      (err) => {
        console.warn('[useStaffAttendance] snapshot error:', err);
        setRows([]);
        setLoading(false);
      },
    );
  }, [orgId, startKey, endKey]);

  const byStaff = useMemo(() => {
    const map: Record<string, StaffAttendance[]> = {};
    for (const r of rows) {
      (map[r.staffId] ||= []).push(r);
    }
    return map;
  }, [rows]);

  return { byStaff, loading };
}
